import { forwardRef, useRef, useImperativeHandle, useMemo } from 'react';
import { Input } from '@/modules/Input';
import { InputRef } from '@/shared/types/input';
import { cn } from '@/shared/utils/cn';
import { isValidHexColor, normalizeHexColor, hexToRgb } from '../../utils';

interface ColorInputProps {
  /** Color en formato hexadecimal */
  color?: string;
  /** Label del input */
  label?: string;
  /** Mensaje de error */
  error?: string;
  /** Texto de ayuda */
  helperText?: string;
  /** Si el componente está deshabilitado */
  disabled?: boolean;
  /** Callback cuando cambia el color */
  onColorChange?: (color: string) => void;
  /** Callback cuando se hace click en el input */
  onClick?: () => void;
  /** Clase personalizada */
  className?: string;
}

/**
 * Calcula el brillo relativo de un color para determinar si usar texto claro u oscuro
 */
const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map((val) => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Determina si el texto debe ser claro u oscuro basado en el color de fondo
 */
const getTextColor = (backgroundColor: string): string => {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return '#000000';

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
  // Si el brillo es mayor a 0.5, usar texto oscuro, sino texto claro
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

/**
 * Componente atom que muestra un input con el color de fondo aplicado
 */
export const ColorInput = forwardRef<InputRef, ColorInputProps>(
  (
    { color = '', label, error, helperText, disabled = false, onColorChange, onClick, className },
    ref
  ) => {
    const inputRef = useRef<InputRef>(null);

    // Color normalizado
    const normalizedColor = color ? normalizeHexColor(color) : '';
    const isValid = normalizedColor ? isValidHexColor(normalizedColor) : false;
    const displayColor = isValid ? normalizedColor : '#000000';

    // Calcular color de texto basado en el brillo del fondo
    const textColor = useMemo(() => {
      if (!isValid || disabled) return undefined;
      return getTextColor(displayColor);
    }, [displayColor, isValid, disabled]);

    // Manejar cambio en el input
    const handleChange = (value: string) => {
      const normalized = normalizeHexColor(value);
      if (isValidHexColor(normalized)) {
        onColorChange?.(normalized);
      } else if (value === '' || value === '#') {
        onColorChange?.(value);
      }
    };

    // Exponer métodos del Input a través de ref
    useImperativeHandle(ref, () => ({
      getValue: () => inputRef.current?.getValue() || '',
      setValue: (value: string) => {
        inputRef.current?.setValue(value);
        handleChange(value);
      },
      focus: () => {
        inputRef.current?.focus();
      },
      blur: () => {
        inputRef.current?.blur();
      },
      clear: () => {
        inputRef.current?.clear();
      },
      getInputElement: () => {
        return inputRef.current?.getInputElement() || null;
      },
    }));

    // Estilos para el input con color de fondo
    const containerStyle = useMemo(() => {
      if (!isValid || disabled || error) return undefined;
      return {
        '--color-input-bg': displayColor,
        '--color-input-text': textColor,
      } as React.CSSProperties;
    }, [displayColor, textColor, isValid, disabled, error]);

    // Clase CSS personalizada para aplicar el color de fondo al input
    const inputWrapperClass = useMemo(() => {
      if (!isValid || disabled || error) return '';
      return 'color-input-with-bg';
    }, [isValid, disabled, error]);

    return (
      <div className={cn('ui-relative', className, inputWrapperClass)} style={containerStyle}>
        <style>
          {`.color-input-with-bg input[data-input-id] {
            background-color: var(--color-input-bg) !important;
            color: var(--color-input-text) !important;
          }`}
        </style>
        <Input
          ref={inputRef}
          label={label}
          error={error}
          helperText={helperText}
          value={normalizedColor}
          onValueChange={handleChange}
          disabled={disabled}
          readOnly={false}
          onClick={onClick}
          placeholder="#000000"
          className={cn('ui-w-full', className)}
          {...({} as Record<string, unknown>)}
        />
      </div>
    );
  }
);

ColorInput.displayName = 'ColorInput';
